import {useCallback, useRef} from 'react';
import {useAppContext} from 'contexts/ApplicationContext';
import {useTutorialMutation} from 'shared/components/Updates/store/updateApi';
import {useDialogContext} from 'contexts/DialogContext';

export const UNDO_REDO_TOOLTIP_KEY = 'room_planner_undo_redo_tooltip';

import 'driver.js/dist/driver.css';
import {Driver, driver} from 'driver.js';

export const useTutorialHelper = () => {
    const {show: dialogIsVisible} = useDialogContext();
    const {userProfile} = useAppContext();
    const [setRoomPlannerTutorial] = useTutorialMutation();
    const driverObject = useRef<Driver>();
    const localTutorialCheck = useRef<Record<string, boolean>>({});

    const checkTutorialOption = useCallback(
        (key: string) => {
            const tutorialOptions = {
                ...userProfile?.tutorialOptions,
                ...localTutorialCheck.current,
            };

            return tutorialOptions && !tutorialOptions.hasOwnProperty(key);
        },
        [userProfile]
    );

    const markTutorialOption = useCallback(
        (options: Record<string, boolean>) => {
            Object.assign(localTutorialCheck.current, options);
            void setRoomPlannerTutorial(options);
        },
        [setRoomPlannerTutorial]
    );

    const hideUndoRedoTutorial = useCallback(() => {
        driverObject.current?.destroy();
    }, []);

    const showUndoRedoTutorial = useCallback(
        (containerId: string) => {
            if (
                checkTutorialOption(UNDO_REDO_TOOLTIP_KEY) &&
                !dialogIsVisible
            ) {
                driverObject.current = driver({
                    popoverClass: 'driverjs-popover',
                    showProgress: false,
                    showButtons: ['close', 'next'],
                    steps: [
                        {
                            element: `#${containerId}`,
                            popover: {
                                title: 'New',
                                description:
                                    'Undo / redo your changes and view your history to step back up to 10 actions at any time',
                            },
                        },
                    ],
                    onDestroyed: () => {
                        void setRoomPlannerTutorial({
                            [UNDO_REDO_TOOLTIP_KEY]: true,
                        });
                        localTutorialCheck.current[
                            String(UNDO_REDO_TOOLTIP_KEY)
                        ] = true;
                    },
                });

                driverObject.current.drive();
            }
        },
        [checkTutorialOption, dialogIsVisible]
    );

    return {
        checkTutorialOption,
        markTutorialOption,
        setRoomPlannerTutorial,
        showUndoRedoTutorial,
        hideUndoRedoTutorial,
    };
};
